/**
 * Extension à l'editor :
 * - fournisseur de diff (drf, drv, histo, otherItem)
 */

import {REG} from "/@lib@/commons/registry.js";
import {DiffBarPanelAction, IDiffBarPanel, IWedDiffBar, JWedDiffBarParams} from "/@back@/edit/wed/features/diffBar.js";
import {EDrvState, srcRef} from "/@lib@/wsp/src.js";
import {EXmlDiffMode} from "/@lib@/edit/schema/diff.js";
import {IWspEnv} from "/@lib@/wsp/wsp.js";
import {IItemUiEnv} from "/@back@/wsp/views/itemMain.js";


/**
 * Atelier draft : item dans l'atelier de ref.
 */
export class DrfRefDiffAction extends DiffBarPanelAction {

	constructor(id?: string) {
		super(id || 'drfRef');
		this._label = "Référence";
	}

	isVisible(ctx: IWedDiffBar): boolean {
		if (!(ctx.editor.reg.env as IWspEnv).wsp.infoWsp.props.drfRefWsp) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IWedDiffBar): boolean {
		if (!super.isEnabled(ctx)) return false;
		const drfState = (ctx.editor.reg.env as IItemUiEnv).longDesc.drfState;
		return drfState && drfState.indexOf("created") < 0;
	}

	async buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel> {
		return new (await import("/@back@/wsp/widgets/wed/wspDiffBar.js")).DrfRefDiffPanel().initPanel(this, ctx);
	}
}

export interface JDrfRefWedDiffBarParams extends JWedDiffBarParams {}

REG.reg.addToList("wed.diffBar.actions", "drfRef", 1, new DrfRefDiffAction());


/**
 * Atelier draft : item dans l'atelier de ref.
 */
export class DrvDiffAction extends DiffBarPanelAction {

	constructor(id: string, public readonly specialRef: string, label: string, public readonly specialCandidateWspCd?: string, public readonly specialBaseWspCd?: string) {
		super(id);
		this._label = label;
	}

	protected drvState(ctx: IWedDiffBar): EDrvState | undefined {
		/*
		non utilisé pr le moment
		const infoWsp = (ctx.editor.reg.env as IWspEnv).wsp.infoWsp || (ctx.editor.reg.env as IWspEnv).wsp.infoWspError;
		if (infoWsp && WSP_DRF.isDrf(infoWsp.props))
			return ((ctx.editor.reg.env as IItemUiEnv).longDesc as JDrvInDrfSrcFields).ref_drvState;
		else
		*/
		return (ctx.editor.reg.env as IItemUiEnv).longDesc.drvState;
	}

	isVisible(ctx: IWedDiffBar): boolean {
		if (!this.drvState(ctx)) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: IWedDiffBar): boolean {
		if (!super.isEnabled(ctx)) return false;
		const drvState = this.drvState(ctx);
		//console.log("drvState:::", drvState);
		return DrvDiffAction.isEnabledForDrvState(this.specialRef, drvState);
	}

	static isEnabledForDrvState(specialRef: string, drvState: EDrvState) {
		switch (specialRef) {
		case "special:drv:default":
			return drvState.indexOf("created") < 0;
		case "special:drv:done":
			return drvState.indexOf("Dirty") >= 0 && drvState.indexOf("notOverriden") < 0;
		case "special:drv:default:done":
			return drvState.indexOf("Dirty") >= 0 && drvState.indexOf("created") < 0;
		}
	}

	async buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel> {
		return new (await import("/@back@/wsp/widgets/wed/wspDiffBar.js")).DrvDiffPanel().initPanel(this, ctx);
	}
}

export interface JDrvWedDiffBarParams extends JWedDiffBarParams {}

REG.reg.addToList("wed.diffBar.actions", "drvDefault", 1, new DrvDiffAction("drvDefault", "special:drv:default", "Contenu d'origine (avant surcharge)"));
REG.reg.addToList("wed.diffBar.actions", "drvDone", 1, new DrvDiffAction("drvDone", "special:drv:done", "Précédent état achevé"));
REG.reg.addToList("wed.diffBar.actions", "drvDefaultDone", 1, new DrvDiffAction("drvDefaultDone", "special:drv:default:done", "Contenu d'origine du précédent état 'achevé'"));

/**
 * Diff issu de l'historique de l'item.
 */
export class HistoItemDiffAction extends DiffBarPanelAction {

	constructor(id?: string) {
		super(id || 'histoItem');
		//this._icon = "/@skin@/edit/wed/diff/otherItem.svg";
		this._label = "Entrée d'historique";
	}

	isVisible(ctx: IWedDiffBar): boolean {
		if (!(ctx.editor.reg.env as IWspEnv).wsp.hasFeature("history")) return false;
		return super.isVisible(ctx);
	}

	async buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel> {
		return new (await import("/@back@/wsp/widgets/wed/wspDiffBar.js")).HistoItemDiffPanel().initPanel(this, ctx);
	}
}

export interface JHistoItemWedDiffBarParams extends JWedDiffBarParams {
	srcDiff: srcRef;
}

REG.reg.addToList("wed.diffBar.actions", "histoItem", 1, new HistoItemDiffAction());


/**
 * Diff avec un autre item.
 */
export class OtherItemDiffAction extends DiffBarPanelAction {

	constructor(id?: string) {
		super(id || 'otherItem');
		//this._icon = "/@skin@/edit/wed/diff/otherItem.svg";
		this._label = "Autre item";
	}

	async buildPanel(ctx: IWedDiffBar, ev?: Event): Promise<IDiffBarPanel> {
		return new (await import("/@back@/wsp/widgets/wed/wspDiffBar.js")).OtherItemDiffPanel().initPanel(this, ctx);
	}
}

export interface JOtherItemWedDiffBarParams extends JWedDiffBarParams {
	srcDiff: srcRef;
	mode: EXmlDiffMode;
}

REG.reg.addToList("wed.diffBar.actions", "otherItem", 1, new OtherItemDiffAction());
